<template>
  <div class="image-stats">
    <div class="header">
      <h2>📊 用户影像处理信息统计</h2>
      <div class="filters">
        <input v-model="keyword" placeholder="搜索昵称 / 手机号 / 邮箱" @keyup.enter="fetchData(1)" />
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="YYYY-MM-DD"
          @change="fetchData(1)"
        />
        <button @click="fetchData(1)">🔍 查询</button>
        <button 
          class="toggle-btn" 
          :class="{ active: showZeroCount }"
          @click="toggleZeroCount"
        >
          {{ showZeroCount ? '隐藏零处理记录' : '显示零处理记录' }}
        </button>
      </div>
    </div>

    <div class="stats-summary">
      <div class="card">
        <h4>📈 处理模块使用统计</h4>
        <div class="stats-grid">
          <div v-for="item in stats.module" :key="item.module_name" class="stat-item">
            <span class="stat-label">{{ item.module_name }}</span>
            <span class="stat-value">{{ item.user_count }} 人</span>
          </div>
        </div>
      </div>
    </div>

    <div class="stats-summary">
      <div class="card satellite-card">
        <h4>🛰️ 卫星载荷使用统计</h4>
        <div class="satellite-modules">
          <div class="module-card radiation">
            <div class="module-header">
              <div class="module-title-section">
                <span class="module-icon">☢️</span>
                <span class="module-title">辐射定标</span>
                <span class="total-usage-inline">共 {{ getModuleTotalUsage('radiation_calibration') }} 次</span>
              </div>
            </div>
            <div class="module-content">
              <div class="payload-list">
                <span v-for="item in getModulePayloads('radiation_calibration')" :key="item.satellite_payload" class="payload-text">
                  {{ item.satellite_payload || '未填写' }}：{{ item.usage_count }} 次
                </span>
              </div>
            </div>
          </div>
          
          <div class="module-card atmospheric">
            <div class="module-header">
              <div class="module-title-section">
                <span class="module-icon">🌤️</span>
                <span class="module-title">大气校正</span>
                <span class="total-usage-inline">共 {{ getModuleTotalUsage('atmospheric_correction') }} 次</span>
              </div>
            </div>
            <div class="module-content">
              <div class="payload-list">
                <span v-for="item in getModulePayloads('atmospheric_correction')" :key="item.satellite_payload" class="payload-text">
                  {{ item.satellite_payload || '未填写' }}：{{ item.usage_count }} 次
                </span>
              </div>
            </div>
          </div>
          
          <div class="module-card geometric">
            <div class="module-header">
              <div class="module-title-section">
                <span class="module-icon">📐</span>
                <span class="module-title">几何校正</span>
                <span class="total-usage-inline">共 {{ getModuleTotalUsage('geometric_correction') }} 次</span>
              </div>
            </div>
            <div class="module-content">
              <div class="payload-list">
                <span v-for="item in getModulePayloads('geometric_correction')" :key="item.satellite_payload" class="payload-text">
                  {{ item.satellite_payload || '未填写' }}：{{ item.usage_count }} 次
                </span>
              </div>
            </div>
          </div>
          
          <div class="module-card onestop">
            <div class="module-header">
              <div class="module-title-section">
                <span class="module-icon">🚀</span>
                <span class="module-title">一站式处理</span>
                <span class="total-usage-inline">共 {{ getModuleTotalUsage('one_stop_process') }} 次</span>
              </div>
            </div>
            <div class="module-content">
              <div class="payload-list">
                <span v-for="item in getModulePayloads('one_stop_process')" :key="item.satellite_payload" class="payload-text">
                  {{ item.satellite_payload || '未填写' }}：{{ item.usage_count }} 次
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="table-section">
      <div class="table-header">
        <h3>用户处理记录 (共 {{ total }} 条)</h3>
        <div class="table-actions">
          <span class="page-info">每页 {{ limit }} 条</span>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>用户ID</th>
            <th>姓名</th>
            <th>手机号</th>
            <th>邮箱</th>
            <th>处理次数</th>
            <th>单位类型</th>
            <th>单位名称</th>
            <th>处理详情</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in filteredUsers" :key="user.id">
            <td>{{ user.id }}</td>
            <td>{{ user.name }}</td>
            <td>{{ user.phone }}</td>
            <td>{{ user.email }}</td>
            <td>
              <span :class="['count-badge', user.processing_count > 0 ? 'has-data' : 'no-data']">
                {{ user.processing_count }}
              </span>
            </td>
            <td>{{ user.work_unit }}</td>
            <td>{{ user.institution_name }}</td>
            <td>
              <div class="processing-details">
                <template v-if="user.processing_details && user.processing_details.length > 0">
                  <div v-for="(item, i) in user.processing_details" :key="i" class="detail-item">
                    <div class="detail-header">
                      <span class="module-name">{{ item.module_name }}</span>
                      <span class="satellite-name">{{ item.satellite_payload }}</span>
                      <span class="satellite-name" v-if="item.model_type">{{ item.model_type }}</span>
                    </div>
                    <div class="detail-content">
                      <div class="file-path">
                        <span class="path-label">处理时间:</span>
                        <span class="path-value">{{ new Date(item.created_at).toLocaleString() }}</span>
                      </div>
                      <div class="file-path">
                        <span class="path-label">输入影像:</span>
                        <span class="path-value">{{ getFileName(item.input_image) }}</span>
                      </div>
                      <div class="file-path">
                        <span class="path-label">输出影像:</span>
                        <span class="path-value">{{ getFileName(item.output_image) }}</span>
                      </div>
                      <div v-if="item.calib_file" class="file-path">
                        <span class="path-label">定标文件:</span>
                        <span class="path-value">{{ getFileName(item.calib_file) }}</span>
                      </div>
                      <div v-if="item.xml_file" class="file-path">
                        <span class="path-label">XML文件:</span>
                        <span class="path-value">{{ getFileName(item.xml_file) }}</span>
                      </div>
                      <div v-if="item.rgb_file" class="file-path">
                        <span class="path-label">RGB文件:</span>
                        <span class="path-value">{{ getFileName(item.rgb_file) }}</span>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <span class="no-data">暂无处理记录</span>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination">
        <button :disabled="page === 1" @click="fetchData(page - 1)">上一页</button>
        <span>第 {{ page }} 页 / 共 {{ totalPages }} 页</span>
        <button :disabled="page === totalPages" @click="fetchData(page + 1)">下一页</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus'

export default {
  name: 'ImageStats',
  data() {
    return {
      keyword: '',
      dateRange: [],
      page: 1,
      limit: 10,
      total: 0,
      users: [],
      stats: {
        module: [],
        payload: []
      },
      showZeroCount: false
    };
  },
  computed: {
    totalPages() {
      const total = parseInt(this.total) || 0;
      const limit = parseInt(this.limit) || 10;
      return Math.max(1, Math.ceil(total / limit));
    },
    filteredUsers() {
      return this.showZeroCount 
        ? this.users 
        : this.users.filter(user => user.processing_count > 0);
    }
  },
  methods: {
    async fetchData(p = 1) {
      this.page = p;
      try {
        const res = await axios.get('/api/admin/stats/image-stats', {
          params: {
            page: this.page,
            limit: this.limit,
            keyword: this.keyword,
            startDate: Array.isArray(this.dateRange) && this.dateRange[0] ? this.dateRange[0] : '',
            endDate: Array.isArray(this.dateRange) && this.dateRange[1] ? this.dateRange[1] : '',
            showZeroCount: this.showZeroCount
          }
        });
        
        if (!res || !res.data) {
          this.users = [];
          this.total = 0;
          this.stats = { module: [], payload: [] };
          return;
        }

        if (res && res.data) {
          this.users = res.data.data || [];
          this.total = res.data.total || 0;
          this.stats = {
            module: res.data.stats?.module || [],
            payload: res.data.stats?.payload || []
          };
        }
      } catch (err) {
        console.error('获取失败', err);
        ElMessage.error(err.response?.data?.message || '获取数据失败');
      }
    },
    toggleZeroCount() {
      this.showZeroCount = !this.showZeroCount;
      this.fetchData(1); // 立即刷新数据
    },
    getFileName(path) {
      return path?.split(/[\\/]/).pop() || '无';
    },
    getTotalPayloadUsage(payloads) {
      if (!payloads || payloads.length === 0) {
        return 0;
      }
      return payloads.reduce((sum, item) => sum + item.usage_count, 0);
    },
    getModuleTotalUsage(moduleName) {
      // 尝试多种可能的模块名称匹配
      let matchedKey = null;
      const availableKeys = Object.keys(this.stats.payload || {});
      
      // 精确匹配
      if (this.stats.payload && this.stats.payload[moduleName]) {
        matchedKey = moduleName;
      } else {
        // 模糊匹配
        for (const key of availableKeys) {
          if (this.matchModuleName(key, moduleName)) {
            matchedKey = key;
            break;
          }
        }
      }
      
      if (matchedKey && this.stats.payload[matchedKey]) {
        const total = this.stats.payload[matchedKey].reduce((sum, item) => sum + item.usage_count, 0);
        return total;
      }
      
      return 0;
    },
    getModulePayloads(moduleName) {
      // 尝试多种可能的模块名称匹配
      let matchedKey = null;
      const availableKeys = Object.keys(this.stats.payload || {});
      
      // 精确匹配
      if (this.stats.payload && this.stats.payload[moduleName]) {
        matchedKey = moduleName;
      } else {
        // 模糊匹配
        for (const key of availableKeys) {
          if (this.matchModuleName(key, moduleName)) {
            matchedKey = key;
            break;
          }
        }
      }
      
      if (matchedKey && this.stats.payload[matchedKey]) {
        // 按处理次数从高到低排序
        const sortedPayloads = [...this.stats.payload[matchedKey]].sort((a, b) => b.usage_count - a.usage_count);
        return sortedPayloads;
      }
      
      return [];
    },
    matchModuleName(dbModuleName, expectedModuleName) {
      // 将数据库中的模块名称转换为小写进行匹配
      const dbName = dbModuleName.toLowerCase();
      const expectedName = expectedModuleName.toLowerCase();
      
      // 精确匹配
      if (dbName === expectedName) return true;
      
      // 关键词匹配
      if (expectedName === 'radiation_calibration' && 
          (dbName.includes('辐射') || dbName.includes('radiation') || dbName.includes('定标'))) {
        return true;
      }
      
      if (expectedName === 'atmospheric_correction' && 
          (dbName.includes('大气') || dbName.includes('atmospheric'))) {
        return true;
      }
      
      if (expectedName === 'geometric_correction' && 
          (dbName.includes('几何') || dbName.includes('geometric'))) {
        return true;
      }
      
      if (expectedName === 'one_stop_process' && 
          (dbName.includes('一站式') || dbName.includes('one_stop') || dbName.includes('one-stop'))) {
        return true;
      }
      
      return false;
    },
    getModuleDisplayName(moduleName) {
      // 根据模块名称返回中文显示名称
      const moduleMap = {
        'radiation_calibration': '辐射校正',
        'atmospheric_correction': '大气校正', 
        'geometric_correction': '几何校正',
        'one_stop_process': '一站式处理',
        'optical': '光学载荷',
        'radar': '雷达载荷',
        'thermal': '热红外载荷'
      };
      
      // 如果模块名称包含关键词，返回对应的中文名称
      if (moduleName.includes('辐射校正') || moduleName.includes('radiation')) {
        return '辐射校正';
      } else if (moduleName.includes('大气校正') || moduleName.includes('atmospheric')) {
        return '大气校正';
      } else if (moduleName.includes('几何校正') || moduleName.includes('geometric')) {
        return '几何校正';
      } else if (moduleName.includes('一站式') || moduleName.includes('one_stop')) {
        return '一站式处理';
      }
      
      // 如果没有匹配到，返回原名称
      return moduleName;
    }
  },
  mounted() {
    this.fetchData();
  }
};
</script>

<style scoped>
.image-stats {
  padding: 12px;
  background: #f4f7fc;
  font-size: 13px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.header h2 {
  font-size: 1.2em;
  color: #2c3e50;
  margin: 0;
}

.filters {
  display: flex;
  gap: 8px;
  align-items: center;
}

.filters :deep(.el-date-editor) {
  width: 320px;
}

.filters :deep(.el-input__wrapper) {
  box-shadow: 0 0 0 1px #dcdfe6 inset;
}

.filters :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #c0c4cc inset;
}

.filters :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #409eff inset;
}

.filters input {
  padding: 6px 10px;
  width: 240px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  margin-right: 8px;
  font-size: 12px;
}

.filters button {
  padding: 6px 12px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.filters button:hover {
  background-color: #66b1ff;
}

.toggle-btn {
  padding: 6px 12px;
  background-color: #909399;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 12px;
}

.toggle-btn:hover {
  background-color: #a6a9ad;
}

.toggle-btn.active {
  background-color: #67c23a;
}

.toggle-btn.active:hover {
  background-color: #85ce61;
}

.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.card {
  background: white;
  padding: 12px;
  border-radius: 6px;
  box-shadow: 0 2px 8px 0 rgba(0,0,0,0.1);
}

.card h4 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 卫星载荷统计卡片特殊样式 */
.card:has(.satellite-stats) {
  grid-column: 1 / -1;
  max-width: none;
}

.card:has(.satellite-stats) h4 {
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e9ecef;
}

/* 新的卫星载荷模块样式 */
.satellite-card {
  grid-column: 1 / -1;
  max-width: none;
}

.satellite-modules {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 12px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .satellite-modules {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
}

@media (max-width: 768px) {
  .satellite-modules {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .module-card {
    height: 100px;
    padding: 10px;
  }
}

.module-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 12px;
  height: 100px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.module-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.module-card.radiation {
  border-left: 4px solid #dc3545;
}

.module-card.atmospheric {
  border-left: 4px solid #17a2b8;
}

.module-card.geometric {
  border-left: 4px solid #28a745;
}

.module-card.onestop {
  border-left: 4px solid #ffc107;
}

.module-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  padding-bottom: 4px;
  border-bottom: 1px solid #dee2e6;
  flex-shrink: 0;
}

.module-title-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.module-icon {
  font-size: 16px;
}

.module-title {
  font-size: 14px;
  color: #495057;
  font-weight: 600;
}

.total-usage-inline {
  font-size: 11px;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 10px;
  white-space: nowrap;
}

.module-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: 0;
  overflow: hidden;
}

/* 移除旧的total-usage样式 */
.total-usage {
  display: none;
}

.payload-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: row;
  gap: 8px;
  padding: 4px 0;
  flex-wrap: wrap;
  align-content: flex-start;
  row-gap: 2px;
}

.payload-text {
  font-size: 11px;
  color: #495057;
  line-height: 1.2;
  padding: 0;
  white-space: nowrap;
}

/* 移除旧的payload-item样式 */
.payload-item {
  display: none;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
}

.stat-item {
  background: #f5f7fa;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
}

.stat-label {
  font-size: 12px;
  color: #303133;
}

.stat-value {
  font-size: 14px;
  color: #409eff;
  font-weight: bold;
  margin-top: 4px;
}

.table-section {
  background: white;
  padding: 12px;
  border-radius: 6px;
  box-shadow: 0 2px 8px 0 rgba(0,0,0,0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.table-header h3 {
  margin: 0;
  color: #303133;
  font-size: 14px;
}

.page-info {
  color: #303133;
  font-size: 12px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

th, td {
  padding: 8px;
  border: 1px solid #ebeef5;
  text-align: left;
}

th {
  background-color: #f5f7fa;
  color: #303133;
  font-weight: 500;
}

.count-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 12px;
}

.count-badge.has-data {
  background-color: #67c23a;
  color: white;
}

.count-badge.no-data {
  background-color: #909399;
  color: white;
}

.processing-details {
  max-height: 200px;
  overflow-y: auto;
}

.detail-item {
  background: #f5f7fa;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.module-name {
  color: #303133;
  font-weight: 500;
  font-size: 12px;
}

.satellite-name {
  color: #303133;
  font-size: 12px;
}

.detail-content {
  font-size: 12px;
}

.file-path {
  margin: 4px 0;
}

.path-label {
  color: #303133;
  margin-right: 4px;
}

.path-value {
  color: #303133;
  word-break: break-all;
}

.no-data {
  color: #303133;
  font-style: italic;
  font-size: 12px;
}

.pagination {
  margin-top: 12px;
  text-align: center;
}

.pagination button {
  margin: 0 6px;
  padding: 6px 12px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.pagination button:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.pagination span {
  color: #303133;
  font-size: 12px;
}

table td {
  color: #000000;
}

table td:nth-child(1),
table td:nth-child(2),
table td:nth-child(3),
table td:nth-child(4),
table td:nth-child(6) {
  color: #000000;
  font-weight: 500;
}
</style>